import React from 'react';
import { Menu, MenuButton } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Tooltip from 'flow/components/ui-components/Tooltip';
import {
    selectTicketIsFetched,
    selectTicketState,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';
import StatePickerMenu from 'flow/components/tickets/State/StatePickerMenu';
import useUpdateTicketState from 'flow/components/tickets/State/useUpdateTicketState';

import TicketHeaderStateButtonFace from './TicketHeaderStateButtonFace';

interface Props {
    ticketKey: TicketKey;
}

const TicketHeaderStateButton = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const ticketStatus = useAppSelector((state) =>
        selectTicketState(state, ticketKey),
    );
    const canSetState = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setState'),
    );
    const ticketIsFetched = useAppSelector((state) =>
        selectTicketIsFetched(state, ticketKey),
    );

    const { onSelect, isPerformingApiRequest } = useUpdateTicketState(
        ticketKey,
        'ticket header',
    );

    if (!ticketIsFetched) return null;

    const disabled = !canSetState || isPerformingApiRequest;

    return (
        <Menu as="div" className="relative">
            {({ open }) => (
                <>
                    <Tooltip
                        label={
                            canSetState
                                ? undefined
                                : i18next.t('tickets.no_permission')
                        }
                    >
                        <MenuButton
                            disabled={disabled}
                            className={twMerge(
                                'flex h-10 items-center gap-1 rounded-lg ps-3 pe-2 text-xs font-semibold',
                                ticketStatus === 'open'
                                    ? 'bg-yellow text-gray-800 dark:text-white'
                                    : 'bg-green text-white',
                                disabled
                                    ? 'pointer-events-none opacity-50'
                                    : 'hover:darken-10',
                            )}
                        >
                            <TicketHeaderStateButtonFace
                                statusKey={ticketStatus}
                            />
                        </MenuButton>
                    </Tooltip>
                    {open ? <StatePickerMenu onSelect={onSelect} /> : null}
                </>
            )}
        </Menu>
    );
};

export default React.memo(TicketHeaderStateButton);
